import React, { useState, useEffect } from 'react';
import { authFetch } from '../../context/AuthContext';
import ProductSearch, { StockProduct } from '../../components/ui/ProductSearch';

interface Supplier { id: number; business_no: string; company_name: string; contact_name?: string; phone?: string; }
interface Item     { product_name: string; quantity: string; unit_price: string; product_id?: number; currentStock?: number; }

interface Props { onClose: () => void; onSuccess: (msg: string) => void; }

const emptyItem = (): Item => ({ product_name: '', quantity: '', unit_price: '', product_id: undefined });

export default function MaterialReceiptModal({ onClose, onSuccess }: Props) {
  const [suppliers,    setSuppliers]    = useState<Supplier[]>([]);
  const [suppSearch,   setSuppSearch]   = useState('');
  const [suppId,       setSuppId]       = useState<number | ''>('');
  const [suppInfo,     setSuppInfo]     = useState<Supplier | null>(null);
  const [receiptDate,  setReceiptDate]  = useState(new Date().toISOString().split('T')[0]);
  const [items,        setItems]        = useState<Item[]>([emptyItem()]);
  const [note,         setNote]         = useState('');
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [newSupp,      setNewSupp]      = useState({ business_no: '', company_name: '', contact_name: '', phone: '' });

  useEffect(() => {
    authFetch(`/api/suppliers${suppSearch ? '?search=' + encodeURIComponent(suppSearch) : ''}`)
      .then(r => r.ok ? r.json() : [])
      .then(setSuppliers);
  }, [suppSearch]);

  const selectSupplier = (s: Supplier) => {
    setSuppId(s.id);
    setSuppInfo(s);
    setSuppSearch('');
  };

  const total = items.reduce((sum, i) => {
    const q = parseInt(i.quantity) || 0;
    const p = parseInt(i.unit_price) || 0;
    return sum + q * p;
  }, 0);

  const setItem = (idx: number, field: keyof Item, val: string) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const removeItem = (idx: number) =>
    setItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  /* 공급업체 신규 등록 */
  const handleAddSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSupp.business_no || !newSupp.company_name) return;
    const res  = await authFetch('/api/suppliers', { method: 'POST', body: JSON.stringify(newSupp) });
    const data = await res.json();
    if (!res.ok) { setError(data.error ?? '등록 실패'); return; }
    setSuppliers(prev => [...prev, data]);
    selectSupplier(data);
    setShowSupplierForm(false);
    setNewSupp({ business_no: '', company_name: '', contact_name: '', phone: '' });
  };

  /* 입고 처리 */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suppId) { setError('공급업체를 선택해주세요.'); return; }
    const validItems = items.filter(i => i.product_name && i.quantity && i.unit_price);
    if (!validItems.length) { setError('품목을 1개 이상 입력해주세요.'); return; }

    setSaving(true); setError('');
    const res  = await authFetch('/api/receipts', {
      method: 'POST',
      body: JSON.stringify({ supplier_id: suppId, receipt_date: receiptDate, items: validItems, note }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setError(data.error ?? '처리 실패'); return; }
    onSuccess(`자재입고 처리 완료 (${data.receipt_no})`);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-3 py-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">

        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-bold text-lg text-stone-900">자재 입고 거래명세표</h2>
            <p className="text-xs text-stone-400 mt-0.5">공급업체에서 입고된 자재를 등록합니다</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-2xl">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* ── 공급자 정보 ── */}
          <div className="border border-stone-200 rounded-xl p-4 bg-stone-50">
            <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">공급자 정보</h3>

            {suppInfo ? (
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-stone-800">{suppInfo.company_name}</p>
                  <p className="text-sm text-stone-500 mt-0.5">사업자번호: {suppInfo.business_no}</p>
                  {suppInfo.contact_name && <p className="text-sm text-stone-500">담당: {suppInfo.contact_name} {suppInfo.phone}</p>}
                </div>
                <button type="button" onClick={() => { setSuppId(''); setSuppInfo(null); }}
                  className="text-xs text-amber-600 hover:underline">변경</button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input value={suppSearch} onChange={e => setSuppSearch(e.target.value)}
                    className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="회사명 또는 사업자번호로 검색..." />
                  <button type="button" onClick={() => setShowSupplierForm(v => !v)}
                    className="bg-amber-500 hover:bg-amber-600 text-black text-xs font-bold px-3 py-2 rounded-lg whitespace-nowrap">
                    + 신규 등록
                  </button>
                </div>
                {suppSearch && suppliers.length > 0 && (
                  <div className="border border-stone-200 rounded-lg overflow-hidden">
                    {suppliers.slice(0, 5).map(s => (
                      <button key={s.id} type="button" onClick={() => selectSupplier(s)}
                        className="w-full text-left px-3 py-2.5 hover:bg-amber-50 text-sm border-b border-stone-100 last:border-0 flex justify-between">
                        <span className="font-medium">{s.company_name}</span>
                        <span className="text-stone-400 text-xs">{s.business_no}</span>
                      </button>
                    ))}
                  </div>
                )}
                {showSupplierForm && (
                  <div className="border border-amber-200 rounded-xl p-4 bg-amber-50 space-y-2">
                    <p className="text-xs font-bold text-amber-700 mb-2">신규 공급업체 등록</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={newSupp.business_no} onChange={e => setNewSupp({...newSupp, business_no: e.target.value})}
                        className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="사업자번호 *" required />
                      <input value={newSupp.company_name} onChange={e => setNewSupp({...newSupp, company_name: e.target.value})}
                        className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="회사명 *" required />
                      <input value={newSupp.contact_name} onChange={e => setNewSupp({...newSupp, contact_name: e.target.value})}
                        className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="담당자명" />
                      <input value={newSupp.phone} onChange={e => setNewSupp({...newSupp, phone: e.target.value})}
                        className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="연락처" />
                    </div>
                    <button type="button" onClick={handleAddSupplier}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-2 rounded-lg text-sm">등록 후 선택</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── 입고 일자 ── */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">입고일자</label>
              <input type="date" value={receiptDate} onChange={e => setReceiptDate(e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" required />
            </div>
            <div className="flex-1 text-right">
              <p className="text-xs text-stone-400 mb-1">명세표 번호</p>
              <p className="text-sm font-mono text-stone-600">
                RCP-{receiptDate.replace(/-/g, '')}-자동생성
              </p>
            </div>
          </div>

          {/* ── 품목 테이블 ── */}
          <div>
            <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">입고 품목</h3>
            <div className="border border-stone-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    <th className="text-left px-3 py-2.5 font-semibold text-stone-600 w-5/12">제품명</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-stone-600 w-2/12">수량</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-stone-600 w-2/12">단가</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-stone-600 w-2/12">금액</th>
                    <th className="w-1/12"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const amt = (parseInt(item.quantity)||0) * (parseInt(item.unit_price)||0);
                    return (
                      <tr key={idx} className="border-b border-stone-100 last:border-0">
                        <td className="px-2 py-1.5 relative">
                          <ProductSearch
                            value={item.product_name}
                            onSelect={(p: StockProduct) =>
                              setItems(prev => prev.map((it, i) => i === idx ? {
                                ...it,
                                product_id:   p.id,
                                product_name: p.name,
                                unit_price:   it.unit_price || String(p.price),
                                currentStock: p.stock,
                              } : it))
                            }
                            onChange={name => setItems(prev => prev.map((it, i) =>
                              i === idx ? { ...it, product_name: name, product_id: undefined, currentStock: undefined } : it
                            ))}
                            placeholder="제품명 검색 또는 직접 입력"
                            showStock={true}
                          />
                          {item.currentStock !== undefined && (
                            <p className="text-xs text-stone-400 px-1">
                              현재 재고: <span className="font-semibold">{item.currentStock}개</span>
                              {' → '}입고 후: <span className="font-semibold text-green-600">
                                {item.currentStock + (parseInt(item.quantity) || 0)}개
                              </span>
                            </p>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={item.quantity} onChange={e => setItem(idx, 'quantity', e.target.value)}
                            className="w-full border-0 bg-transparent focus:outline-none focus:bg-amber-50 rounded px-1 py-0.5 text-sm text-right"
                            placeholder="0" min="1" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={item.unit_price} onChange={e => setItem(idx, 'unit_price', e.target.value)}
                            className="w-full border-0 bg-transparent focus:outline-none focus:bg-amber-50 rounded px-1 py-0.5 text-sm text-right"
                            placeholder="0" min="0" />
                        </td>
                        <td className="px-3 py-1.5 text-right text-stone-700 font-medium">
                          {amt > 0 ? amt.toLocaleString() : '-'}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button type="button" onClick={() => removeItem(idx)}
                            className="text-stone-300 hover:text-red-400 text-lg leading-none">×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-50 border-t-2 border-amber-200">
                    <td colSpan={3} className="px-3 py-2.5 text-right font-bold text-stone-700">합계 금액</td>
                    <td className="px-3 py-2.5 text-right font-bold text-amber-700 text-base">{total.toLocaleString()}원</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <button type="button" onClick={() => setItems(prev => [...prev, emptyItem()])}
              className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1">
              <span className="text-lg leading-none">+</span> 품목 추가
            </button>
          </div>

          {/* ── 비고 ── */}
          <div>
            <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">비고</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
              placeholder="특이사항 입력 (선택)" />
          </div>

          {error && <p className="text-red-500 text-sm bg-red-50 px-4 py-3 rounded-xl">{error}</p>}

          {/* ── 버튼 ── */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-stone-200 py-3 rounded-xl text-sm hover:bg-stone-50 transition">취소</button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl text-sm transition">
              {saving ? '처리 중...' : `자재 입고 처리 (${total.toLocaleString()}원)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
